// @ts-nocheck
import { convertDateOnlyStringToDate } from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { ShipmentStatusModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TagWrapper, { TagWrapperProps } from "@store/Library/Components/Toolkit/TagWrapper";
import constants from "@store/constants";
import { getShipmentStatusesDataView } from "@store/Framework/Store/Greif/Data/ShipmentStatusMapping/ShipmentStatusesSelectors";
import updateSearchFields from "@store/Framework/Store/Greif/Pages/ShipmentHistory/Handlers/UpdateSearchFields";
import { historyFilterProps } from "@store/Themes/common/props/history";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    getShipmentsParameter: state.greif.pages.shipmentHistory.getShipmentsParameter,
    shipmenetStatusesDataViewValues: getShipmentStatusesDataView(state).value,
    language: state.context.session.language,
});

const mapDispatchToProps = {
    updateSearchFields,
};

export interface OrderHistorySearchTagsStyles {
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    tagWrapper?: TagWrapperProps;
}

export const shipmentHistorySearchTagsStyles: OrderHistorySearchTagsStyles = {
    tagWrapper: historyFilterProps.tagWrapper,
};

const styles = shipmentHistorySearchTagsStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ShipmentHistorySearchTags = ({
    getShipmentsParameter,
    shipmenetStatusesDataViewValues,
    updateSearchFields,
    language,
}: Props) => {
    const [shipmentStatusMappings, setShipmentStatusMappings] = useState<ShipmentStatusModel[]>([]);

    useEffect(() => {
        if (shipmenetStatusesDataViewValues) {
            setShipmentStatusMappings(shipmenetStatusesDataViewValues);
        }
    }, [shipmenetStatusesDataViewValues]);

    const fieldRemoveHandler = (fieldName: string, value = "") => {
        updateSearchFields({ [fieldName]: value });
    };

    const removeStatusHandler = (fieldName: string, statusName: string) => {
        const _deliveryStatuses = [...(getShipmentsParameter?.deliveryStatuses || [])];
        const _statusIndex = _deliveryStatuses?.indexOf(statusName);

        if (_statusIndex === -1) {
            return;
        }

        _deliveryStatuses?.splice(_statusIndex, 1);
        _deliveryStatuses.length === 0
            ? fieldRemoveHandler(fieldName)
            : updateSearchFields({ [fieldName]: _deliveryStatuses });
    };

    const getStatusDisplay = (status: string) => {
        return shipmentStatusMappings.find(x => x.name === status)?.description || translate("N/A");
    };

    return (
        <TagWrapper {...styles.tagWrapper}>
            {getShipmentsParameter.greifOrderNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("greifOrderNumber");
                    }}
                >
                    {`${translate("Greif Order #")}: ${getShipmentsParameter.greifOrderNumber}`}
                </Tag>
            )}
            {getShipmentsParameter.purchaseOrderNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("purchaseOrderNumber");
                    }}
                >
                    {`${translate("PO #")}: ${getShipmentsParameter.purchaseOrderNumber}`}
                </Tag>
            )}
            {getShipmentsParameter.customerSequence && getShipmentsParameter.customerSequence !== "-1" && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("customerSequence", "-1");
                    }}
                >
                    {`${translate("Ship To")}: ${getShipmentsParameter.customerSequence}`}
                </Tag>
            )}
            {getShipmentsParameter.deliveryStatuses &&
                getShipmentsParameter.deliveryStatuses.map(status => (
                    <Tag
                        key={status}
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            removeStatusHandler("deliveryStatuses", status);
                        }}
                    >
                        {`${translate("Status")}: ${getStatusDisplay(status)}`}
                    </Tag>
                ))}
            {getShipmentsParameter.fromDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("fromDate");
                    }}
                    data-test-selector="shipmentHistoryTags_FromDate"
                >
                    {`${translate("{0}:", "Delivery Date From")} ${getLocalizedDateTime({
                        dateTime: convertDateOnlyStringToDate(getShipmentsParameter.fromDate)!,
                        language,
                    })}`}
                </Tag>
            )}
            {getShipmentsParameter.toDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("toDate");
                    }}
                >
                    {`${translate("{0}:", "Delivery Date To")} ${getLocalizedDateTime({
                        dateTime: convertDateOnlyStringToDate(getShipmentsParameter.toDate)!,
                        language,
                    })}`}
                </Tag>
            )}
            {getShipmentsParameter.isNextDaysFilter && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("isNextDaysFilter");
                    }}
                >
                    {translate("Next 7 Days Shipments")}
                </Tag>
            )}
        </TagWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShipmentHistorySearchTags),
    definition: {
        group: constants.widgetGroup.shipmentHistory,
        displayName: "Tags",
        allowedContexts: [constants.pageType.shipmentHistory],
    },
};

export default widgetModule;
